import React, { useCallback, useState, useMemo, useEffect } from "react";
import {
  Calendar,
  LocalTheme,
  NewButton,
  NewButtonSize,
  TextField,
} from "@bale/ui";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import Footer from "components/commons/footer/Footer";
import Header, { NavType } from "components/commons/header/Header";
import Wrapper from "components/commons/wrapper/Wrapper";
import { NavigationActions, AppPage } from "core/navigation/navigation.slice";
import { strings } from "intl/fa";
import { useAppDispatch, useAppSelector } from "utils/store";
import styles from "./AdTimeSelect.module.scss";
import { useTheme } from "@emotion/react";
import {
  epochToDateType,
  JalaliToRawPersian,
  numberToHour,
  OneDayInMS,
} from "utils/time";
import { PeriodCapacityDataActions } from "core/periodCapacityData/periodCapacityData.slice";
import { toast } from "react-toastify";
import { digitsToHindi } from "@bale/utils";
import CapacityDatePicker from "components/modals/capacityDatePicker/CapacityDatePicker";
import { AdPanelDropdown } from "../../../commons/dropdown/AdPanelDropdown";
import { useConfigs } from "hooks/useConfigs";
import { LARGE_ORDER_BREAKPOINT } from "utils/constants";
import { AdSpot } from "core/orderAd/orderAd.entity";

export interface HourItem {
  hour: string;
  label: string;
  disabled?: boolean;
}

const AdTimeSelect: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [isSelectDateModalOpen, setIsSelectDateModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<any>();
  const [selectedDate, setSelectedDate] = useState<number>();

  const dispatch = useAppDispatch();

  const spot = useAppSelector((state) => state.ordersList.selectedOrder?.spot);
  const disableCapacity = spot === AdSpot.AdSpotSponsoredMessage;

  const { set_view, set_click } = useAppSelector(
    (state) => state.ordersList.selectedOrder!
  );
  const configs = useConfigs();
  const capacityData = useAppSelector(
    (state) => state.periodCapacityData.capacityData
  ).data;

  const selectedTime = useAppSelector(
    (state) => state.periodCapacityData.selectedTime
  );

  const selectedOrder = useAppSelector(
    (state) => state.ordersList.selectedOrder
  );

  const orderData = useAppSelector((state) => state.ordersList.selectedOrder);

  useEffect(() => {
    setSelectedDate(selectedTime?.date);
  }, []);

  const selectableSegments = useMemo(
    () =>
      capacityData.length !== 0 && selectedDate
        ? configs.ad_start_hours.map((segment, index) => {
            const selectedDateCapDataIndex = capacityData.findIndex(
              (capData) => capData.date === selectedDate
            );
            let capacityUsed =
              capacityData[selectedDateCapDataIndex]?.segment_capacity_used[
                index
              ] || 0;

            if (selectedOrder?.spot === AdSpot.AdSpotSponsoredMessage) {
              capacityUsed = 0;
            }

            let labelText = "";

            if (configs.start_hours_capacity[index] <= capacityUsed) {
              labelText = strings.ad_time_select.no_capacity;
            } else if (
              configs.start_hours_capacity[index] * 0.8 <=
              capacityUsed
            ) {
              labelText = strings.ad_time_select.capacity_is_being_filled;
            }

            return {
              hour: String(segment),
              label: labelText,
            };
          })
        : [],
    [selectedDate]
  );

  const theme = useTheme();
  const returnToDetailsPage = useCallback(() => {
    dispatch(NavigationActions.setAppPage(AppPage.OrderDetail));
  }, []);

  const inputsAreValid = Boolean(selectedHour && selectedDate);

  function handleSubmitTime() {
    if (!inputsAreValid) {
      toast.info(strings.ad_time_select.start_time_not_selected);
      return;
    }
    dispatch(
      PeriodCapacityDataActions.setSelectedTime({
        date: selectedDate,
        hour: +selectedHour,
      })
    );
    returnToDetailsPage();
  }

  const onSelectedHourChange = useCallback((hour: HourItem) => {
    setSelectedHour(hour.hour);
  }, []);

  // large orders handling:
  let isLargeDialogOrder: boolean = false;
  let largeOrderEnd;
  let calenderText = "";
  if (selectedOrder && selectedOrder.spot) {
    isLargeDialogOrder =
      selectedOrder?.set_view > LARGE_ORDER_BREAKPOINT ||
      (selectedOrder?.set_click * configs.click_to_view_coef) / 1000 >
        LARGE_ORDER_BREAKPOINT;
  }

  if (selectedDate) {
    largeOrderEnd = epochToDateType(
      (selectedDate || 0) +
        Math.ceil(set_view / LARGE_ORDER_BREAKPOINT) * OneDayInMS -
        1
    );
    calenderText = digitsToHindi(
      isLargeDialogOrder
        ? `${JalaliToRawPersian(
            epochToDateType(selectedDate)
          )} - ${JalaliToRawPersian(largeOrderEnd)}`
        : JalaliToRawPersian(epochToDateType(selectedDate))
    );
  }

  const selectedItemFormatter = (hourItem: HourItem) =>
    isLargeDialogOrder
      ? digitsToHindi(
          strings.ad_time_select.hour +
            numberToHour(hourItem.hour) +
            ` (${JalaliToRawPersian(epochToDateType(selectedDate!))})`
        )
      : strings.ad_time_select.hour +
        digitsToHindi(numberToHour(hourItem.hour));

  return (
    <>
      <Wrapper>
        <Header
          onClose={returnToDetailsPage}
          navType={NavType.both}
          title={strings.ad_time_select.time_select_header}
        />
        <CardWrapper className={styles.Card}>
          <div className={`${styles.FormItem} ${styles.TextItem}`}>
            <div className={styles.NoticeWrapper}>
              <div className={styles.Notice}>
                {strings.ad_time_select.choose_ad_date}{" "}
              </div>
              <div className={styles.Notice}>
                {strings.ad_time_select.reserve_on_select_date}{" "}
              </div>
            </div>
          </div>
          <div className={styles.FormItem}>
            <TextField
              value={calenderText}
              onChange={() => {}}
              readOnly={true}
              suffix={
                <Calendar
                  className={styles.CalendarIcon}
                  size={20}
                  color={(theme as LocalTheme).colors.dark[300]}
                />
              }
              onFocus={() => setIsSelectDateModalOpen(true)}
              label={
                isLargeDialogOrder
                  ? strings.ad_time_select.show_period
                  : strings.ad_time_select.start_day
              }
              placeholder={strings.ad_time_select.select_day}
              helpText={
                isLargeDialogOrder
                  ? strings.ad_time_select.large_order_takes_time
                  : undefined
              }
            />
          </div>
          <div className={styles.FormItem}>
            <AdPanelDropdown<HourItem>
              items={selectableSegments}
              label={strings.ad_time_select.start_hour}
              onSelectedItemChange={onSelectedHourChange}
              itemToString={(i) => digitsToHindi(numberToHour(i?.hour ?? ""))}
              itemToLabel={(i) => (disableCapacity ? "" : i.label)}
              placeholderMessage={strings.ad_time_select.select_hour}
              selectedItemFormatter={(hourItem) =>
                selectedItemFormatter(hourItem)
              }
              isDisable={
                disableCapacity
                  ? (i) => false
                  : (i) => i.label === strings.ad_time_select.no_capacity
              }
              hasWarning={(i) =>
                i.label === strings.ad_time_select.capacity_is_being_filled
              }
              key={selectedDate}
            />
          </div>
        </CardWrapper>
        <Footer>
          <NewButton
            style={{ width: "100%", maxWidth: 336 }}
            onClick={handleSubmitTime}
            text={strings.common.button.submit}
            size={NewButtonSize.LARGE}
            disabled={!inputsAreValid}
          />
        </Footer>
      </Wrapper>
      {isSelectDateModalOpen && (
        <CapacityDatePicker
          onSubmit={(selected) => {
            setSelectedDate(selected);
          }}
          onClose={() => setIsSelectDateModalOpen(false)}
          previewMode={false}
          helpText={
            isLargeDialogOrder
              ? strings.ad_time_select.large_order_takes_time_choose
              : undefined
          }
          finalViewCount={set_view}
          isSponsored={orderData?.spot === AdSpot.AdSpotSponsoredMessage}
          disableCapacity={disableCapacity}
          showButton={true}
        />
      )}
    </>
  );
};

export default AdTimeSelect;
